import type { HikerConfig } from "../../config/types";
import type { TrailSlice } from "../surface";
import { distance, isApproaching, runFrame, type Agent, type RiderView } from "./types";

export type HikerState = "walk" | "startled" | "stepAside" | "waiting";

export interface Hiker extends Agent {
    state: HikerState;
    dirX: number;
    dirY: number;
    timer: number;
    age: number;
}

export function createHiker(x: number, y: number, alongTrail: boolean): Hiker {
    return {
        x, y, pose: "walk_a", flipX: false, state: "walk",
        dirX: alongTrail ? 0 : 1, dirY: alongTrail ? 1 : 0, timer: 0, age: 0,
    };
}

export function hitHiker(hiker: Hiker, rider: RiderView, cfg: HikerConfig): Hiker {
    return { ...hiker, state: "startled", timer: cfg.leapS, dirX: hiker.x >= rider.x ? 1 : -1, dirY: 0 };
}

const onTrail = (h: Hiker, trail: TrailSlice | null) => !!trail && Math.abs(h.x - trail.x) < trail.halfWidth;

export function stepHiker(h: Hiker, rider: RiderView, trail: TrailSlice | null, dt: number, cfg: HikerConfig): Hiker {
    const s = { ...h, age: h.age + dt, timer: Math.max(0, h.timer - dt) };
    const near = isApproaching(s, rider);

    switch (s.state) {
        case "walk":
            if (near && rider.bellRung && distance(s, rider) < cfg.bellRangeM && onTrail(s, trail)) {
                return { ...s, state: "stepAside", dirX: s.x >= trail!.x ? 1 : -1, dirY: 0 };
            }
            if (near && distance(s, rider) < cfg.startleM) return hitHiker(s, rider, cfg);
            return {
                ...s,
                x: s.x + s.dirX * cfg.walkSpeed * dt,
                y: s.y + s.dirY * cfg.walkSpeed * dt,
                pose: runFrame(s.age, 2, "walk_a", "walk_b"),
                flipX: s.dirX > 0,
            };

        case "startled":
            if (s.timer === 0) return { ...s, state: "walk", dirX: 1, dirY: 0 };
            return { ...s, x: s.x + s.dirX * (cfg.leapM / cfg.leapS) * dt, pose: "startled", flipX: s.dirX > 0 };

        case "stepAside": {
            const clearOf = trail ? trail.halfWidth + cfg.clearMarginM : 0;
            if (!trail || Math.abs(s.x - trail.x) >= clearOf) return { ...s, state: "waiting", pose: "walk_a" };
            return { ...s, x: s.x + s.dirX * cfg.asideSpeed * dt, pose: runFrame(s.age, 4, "walk_a", "walk_b"), flipX: s.dirX > 0 };
        }

        case "waiting":
            return rider.y > s.y + 5 ? { ...s, state: "walk" } : s;
    }
}
